// 지역1(이끼 유적) 스테이지 지도 4장 + 좌우 반전 2장(S2, docs/STAGE-WORLD-PRD.md 2.2). 스테이지 1-1~1-10이 6가지 판으로 보인다.
//   A  절벽 테라스 유적 — 굽은 길(북 → 서 꺾임), 낭떠러지 위 아치 다리 둘, 가운데가 뚫린 방, 큰 타원 아레나    (1-1·1-2)
//   A′ A 좌우 반전 + 해질녘 + 입구 후보 반전                                                                 (1-3·1-4)
//   M  고블린 전쟁 캠프 — 목책 마당 → 바위를 낀 갈림길 → 망루 문간 → 원형 목책 투기장(뼈 왕좌)                    (1-5)
//   B  수로 광장 — 동 → 서, 물길 옆 좁은 길 · 원형 광장 2 · 못을 낀 갈림길. 바깥은 낭떠러지가 아니라 물           (1-6·1-7)
//   B′ B 좌우 반전(서 → 동) + 아침 안개                                                                        (1-8·1-9)
//   F  고목 성소 — 뿌리 타원방 → 가운데가 꺼진 둥근 방 → 큰 타원방 → 낭떠러지 위 원형 뿌리 판(녹색 룬 고리)       (1-10)
// 흐름 규약은 R1과 같다: 입구 단 → 전투방 3 → 보스/아레나 1, 방마다 gateIn/gateOut, 입구 문은 열려 있고 출구 문은 정리하면 열린다.
// 게임 판정은 1m 격자(levels.rasterize)다. 낭떠러지·물·가장자리 모양은 렌더 전용이다(edgeStyle·edgeZones·water·sets).
// 좌표: y 위, 바닥 XZ 평면, 북쪽(화면 위) = -z. 방 area는 그 방 바닥을 감싸는 사각형이고 문 줄은 area 바깥에 닿아 있다.
import { Cell, rasterize, type BossKey, type EdgeZone, type ExitDef, type Landmark, type MapDef, type Rect, type RegionId, type RoomDef, type SetPieceDef, type SetPieceKind, type StageMapKey, type WaveDef } from './levels';
import { cellsOf, chamfer, disk, ellipse, mirrorRect, mirrorRects, ring, subtract, union } from './shapes';

const r = (x0: number, z0: number, x1: number, z1: number): Rect => ({ x0, z0, x1, z1 });
const N = Math.PI; // facing: 북쪽(-z)
const W = -Math.PI / 2; // facing: 서쪽(-x)

// ═══ 좌우 반전 ═════════════════════════════════════════════════════
function mirrorZone(z: EdgeZone): EdgeZone { return { rect: mirrorRect(z.rect), style: z.style }; }
function mirrorSet(s: SetPieceDef): SetPieceDef { return { ...s, x: -s.x, rot: -s.rot }; }
/** x 좌우 반전 변형: 바닥·랜드마크·문·방·보스 위치·입구 후보·세트피스를 한 번에 뒤집는다(기능 목록: levels.test.ts 대칭 검사) */
export function mirrorMap(m: MapDef, key: StageMapKey, extra: Partial<MapDef> = {}): MapDef {
  return {
    ...m,
    ...extra,
    stageKey: key,
    mirrored: true,
    floors: mirrorRects(m.floors),
    landmarks: m.landmarks.map((l) => ({ ...l, rect: mirrorRect(l.rect) })),
    gates: m.gates.map((g) => ({ ...g, rect: mirrorRect(g.rect) })),
    rooms: m.rooms.map((rm) => ({
      ...rm,
      area: mirrorRect(rm.area),
      bossAt: rm.bossAt ? { x: -rm.bossAt.x, z: rm.bossAt.z } : undefined,
      backline: rm.backline ? mirrorRect(rm.backline) : undefined,
      entries: rm.entries?.map((e) => ({ x: -e.x, z: e.z })),
      focus: rm.focus ? { x: -rm.focus.x, z: rm.focus.z } : undefined,
    })),
    exits: m.exits.map((e) => ({ ...e, rect: mirrorRect(e.rect) })),
    spawn: { x: -m.spawn.x, z: m.spawn.z, facing: -m.spawn.facing },
    chasms: m.chasms ? mirrorRects(m.chasms) : undefined,
    lava: m.lava ? mirrorRects(m.lava) : undefined,
    vents: m.vents?.map((v) => ({ ...v, x: -v.x })),
    water: m.water ? mirrorRects(m.water) : undefined,
    edgeZones: m.edgeZones?.map(mirrorZone),
    sets: m.sets?.map(mirrorSet),
  };
}

/** 지역1 스테이지 지도 키(순서 고정) */
export const R1_STAGE_MAP_KEYS: readonly StageMapKey[] = ['a', 'a2', 'm', 'b', 'b2', 'f'];

/** 스테이지 번호(1..10) → 지역1 지도 키. 1·2 A, 3·4 A′, 5 M, 6·7 B, 8·9 B′, 10 F */
export function r1StageMapKey(index: number): StageMapKey {
  if (index <= 2) return 'a';
  if (index <= 4) return 'a2';
  if (index === 5) return 'm';
  if (index <= 7) return 'b';
  if (index <= 9) return 'b2';
  return 'f';
}

// ═══ 지연 계산 ═══════════════════════════════════════════════════════
// 지도 데이터(바닥 도형 합성·원경 세트피스·격자)는 모듈 최상위에서 계산하면 Worker 시작 CPU를 약 350ms 먹는다
// (36차 배포 첫 시도가 Cloudflare 10021로 거절됨). 그래서 아래 빌더 함수 안에서 처음 접근할 때 한 번만 계산한다.
// 서버(Worker)는 요청 시점에야 지도를 쓰고, 클라이언트는 첫 접근 때 한 번 계산한다. 지도 내용은 바뀌지 않는다(stageMapsLazy.test.ts 해시로 고정).
function buildStageMaps() {

// 전투방 웨이브(R1 표를 지도 흐름에 맞게 조금 바꾼다 — 스테이지 계단 배율은 stages.ts scaleWaves가 곱한다)
const WAVES_1: readonly WaveDef[] = [{ counts: [16, 0, 0] }, { counts: [12, 3, 0] }];
const WAVES_2: readonly WaveDef[] = [{ counts: [18, 3, 0] }, { counts: [14, 5, 2] }, { counts: [16, 2, 3], elites: 1 }];
const WAVES_3: readonly WaveDef[] = [{ counts: [26, 0, 0] }, { counts: [18, 6, 2] }, { counts: [24, 6, 4], elites: 2 }];
const WAVES_B1: readonly WaveDef[] = [{ counts: [14, 2, 0] }, { counts: [10, 4, 2] }];
const WAVES_B2: readonly WaveDef[] = [{ counts: [20, 0, 0] }, { counts: [14, 6, 2] }, { counts: [14, 4, 4], elites: 1 }];
const WAVES_B3: readonly WaveDef[] = [{ counts: [22, 2, 0] }, { counts: [16, 6, 3] }, { counts: [20, 6, 5], elites: 2 }];

/**
 * 방 둘레 입구 후보: 걸을 수 있고 빈칸에서 3~4칸 떨어진 칸을 방 가운데 둘레 각도순으로 모아 count곳을 고르게 뽑는다.
 * 사각형이 아닌 방(원형·굽은 방·구멍 난 방)에서도 몹이 바닥 위에서 나오게 한다(flow.ts chooseEntries).
 */
function entriesOf(floor: readonly Rect[], area: Rect, solids: readonly Rect[], count = 16): { x: number; z: number }[] {
  const cells = cellsOf(floor);
  for (const k of cellsOf(solids)) cells.delete(k);
  const has = (x: number, z: number): boolean => cells.has(x + ',' + z);
  const dist = (x: number, z: number): number => {
    for (let d = 1; d <= 5; d++) {
      for (let dz = -d; dz <= d; dz++) for (let dx = -d; dx <= d; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== d) continue;
        if (!has(x + dx, z + dz)) return d;
      }
    }
    return 6;
  };
  const cx = (area.x0 + area.x1) / 2, cz = (area.z0 + area.z1) / 2;
  const cand: { a: number; x: number; z: number }[] = [];
  for (const k of cells) {
    const [x, z] = k.split(',').map(Number);
    const d = dist(x, z);
    if (d < 3 || d > 4) continue;
    cand.push({ a: Math.atan2(z + 0.5 - cz, x + 0.5 - cx), x: x + 0.5, z: z + 0.5 });
  }
  cand.sort((p, q) => p.a - q.a || p.x - q.x || p.z - q.z);
  if (cand.length <= count) return cand.map(({ x, z }) => ({ x, z }));
  const out: { x: number; z: number }[] = [];
  for (let i = 0; i < count; i++) { const c = cand[Math.floor(((i + 0.5) / count) * cand.length)]; out.push({ x: c.x, z: c.z }); }
  return out;
}

/** 방 바닥(랜드마크 제외) 칸의 무게중심에 가장 가까운 칸 중심 */
function focusOf(floor: readonly Rect[], area: Rect, solids: readonly Rect[]): { x: number; z: number } {
  const cells = cellsOf(floor);
  for (const k of cellsOf(solids)) cells.delete(k);
  let sx = 0, sz = 0, n = 0;
  const inside: [number, number][] = [];
  for (const k of cells) {
    const [x, z] = k.split(',').map(Number);
    if (x < area.x0 || x >= area.x1 || z < area.z0 || z >= area.z1) continue;
    sx += x + 0.5; sz += z + 0.5; n++; inside.push([x, z]);
  }
  const cx = n ? sx / n : (area.x0 + area.x1) / 2, cz = n ? sz / n : (area.z0 + area.z1) / 2;
  let best = Infinity, bx = cx, bz = cz;
  for (const [x, z] of inside) {
    // 주변 2칸이 모두 바닥인 칸을 우선한다(벽·구멍 곁이 아닌 가운데)
    if (!cells.has(x + 1 + ',' + z) || !cells.has(x - 1 + ',' + z) || !cells.has(x + ',' + (z + 1)) || !cells.has(x + ',' + (z - 1))) continue;
    const d = Math.hypot(x + 0.5 - cx, z + 0.5 - cz);
    if (d < best) { best = d; bx = x + 0.5; bz = z + 0.5; }
  }
  return { x: bx, z: bz };
}

interface RoomSpec {
  readonly name: string;
  readonly kind: 'battle' | 'boss';
  readonly floor: readonly Rect[];
  readonly area: Rect;
  readonly gateIn: number;
  readonly gateOut: number;
  readonly waves: readonly WaveDef[];
  readonly bossAt?: { x: number; z: number };
  readonly boss?: BossKey;
  /** 후열 구역(지역2: 주술사가 나와 지키는 곳) */
  readonly backline?: Rect;
}
function roomOf(s: RoomSpec, solids: readonly Rect[]): RoomDef {
  return { name: s.name, kind: s.kind, area: s.area, gateIn: s.gateIn, gateOut: s.gateOut, waves: s.waves, bossAt: s.bossAt, boss: s.boss, backline: s.backline, entries: entriesOf(s.floor, s.area, solids, s.kind === 'battle' ? 16 : 20), focus: focusOf(s.floor, s.area, solids) };
}

/** 시드 고정 난수(0..1) */
function lcg(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (Math.imul(a, 1664525) + 1013904223) >>> 0; return a / 4294967296; };
}
interface FarOpt {
  /** 암주·폭포·수도교 수 */
  readonly cliffs: number;
  readonly falls: number;
  readonly aqueducts: number;
  /** 바닥에서 이만큼(m) 떨어진 점만 쓴다 */
  readonly minD: number;
  readonly maxD: number;
  /** 같은 종류 사이 최소 간격(m) */
  readonly sep: number;
  /** 암주 발 높이(m, 꼭대기 ≈ y + 33) · 수도교 발 높이 */
  readonly yCliff: number;
  readonly yAqueduct: number;
  /** 원경 조각 종류(없으면 지역1: 암주·폭포·수도교). 지역2 = 현무암 첨탑·용암 폭포·아치 돌다리 */
  readonly kinds?: { readonly cliff: SetPieceKind; readonly fall: SetPieceKind; readonly aqueduct: SetPieceKind; readonly fallTop: number; readonly aqueductFalls?: number; readonly seaY?: number };
}
/**
 * 원경 세트피스 배치(렌더 전용 데이터): 바닥에서 minD~maxD m 떨어진 4m 격자 점 중 서로 sep m 이상 떨어진 곳에 암주를 세우고,
 * 그 가운데 일부는 바닥 쪽 면에 폭포를 걸고, 일부 점에는 수도교를 눕힌다. 같은 시드면 같은 배치다(a2·b2는 반전해서 쓴다).
 */
function farSets(floors: readonly Rect[], seed: number, o: FarOpt): SetPieceDef[] {
  const rnd = lcg(seed);
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const f of floors) { x0 = Math.min(x0, f.x0); x1 = Math.max(x1, f.x1); z0 = Math.min(z0, f.z0); z1 = Math.max(z1, f.z1); }
  // 바닥 사각형까지 거리(가까운 바닥 점 방향 포함)
  const nearest = (x: number, z: number): { d: number; dx: number; dz: number } => {
    let best = Infinity, bx = 0, bz = 0;
    for (const f of floors) {
      const cx = Math.max(f.x0, Math.min(f.x1, x)), cz = Math.max(f.z0, Math.min(f.z1, z));
      const d = Math.hypot(x - cx, z - cz);
      if (d < best) { best = d; bx = cx; bz = cz; }
    }
    return { d: best, dx: bx - x, dz: bz - z };
  };
  const cand: { x: number; z: number; d: number; dx: number; dz: number }[] = [];
  const M = o.maxD + 4;
  for (let z = z0 - M; z <= z1 + M; z += 4) for (let x = x0 - M; x <= x1 + M; x += 4) {
    const n = nearest(x, z);
    if (n.d >= o.minD && n.d <= o.maxD) cand.push({ x, z, d: n.d, dx: n.dx, dz: n.dz });
  }
  // 섞기(피셔–예이츠)
  for (let i = cand.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); const t = cand[i]; cand[i] = cand[j]; cand[j] = t; }
  const picks: typeof cand = [];
  for (const c of cand) {
    if (picks.length >= o.cliffs + o.aqueducts) break;
    if (picks.every((p) => Math.hypot(p.x - c.x, p.z - c.z) >= o.sep)) picks.push(c);
  }
  const out: SetPieceDef[] = [];
  const K = o.kinds ?? { cliff: 'far_cliff', fall: 'waterfall', aqueduct: 'far_aqueduct', fallTop: 31 };
  picks.forEach((c, i) => {
    const round = (v: number): number => Math.round(v * 100) / 100;
    const toward = Math.atan2(c.dx, c.dz);
    if (i < o.cliffs) {
      const sc = 0.95 + rnd() * 0.5;
      // 난수 순서(회전 → 높이)는 지역1 배치가 바뀌지 않게 그대로 둔다
      const rot = round(rnd() * Math.PI * 2), y = round(o.yCliff - rnd() * 4);
      out.push({ kind: K.cliff, x: round(c.x), z: round(c.z), rot, scale: round(sc), y });
      if (i < o.falls) {
        // 폭포는 암주의 바닥 쪽 면(암주 반지름 안쪽)에 건다. 용암 폭포는 첨탑 꼭대기에서 용암 바다까지 떨어진다(len = 낙차)
        const len = Math.hypot(c.dx, c.dz) || 1;
        const top = round(o.yCliff + K.fallTop * sc);
        out.push({ kind: K.fall, x: round(c.x + (c.dx / len) * 6.5 * sc), z: round(c.z + (c.dz / len) * 6.5 * sc), rot: round(toward), scale: round(0.9 + rnd() * 0.5), y: top, ...(K.seaY !== undefined ? { len: round(top - K.seaY) } : {}) });
      }
    } else {
      const rot = round(Math.round(rnd() * 4) * (Math.PI / 2) + rnd() * 0.2), sc = round(0.8 + rnd() * 0.3);
      out.push({ kind: K.aqueduct, x: round(c.x), z: round(c.z), rot, scale: sc, y: o.yAqueduct });
      // 아치 돌다리 가운데 아치 밑으로 떨어지는 용암 줄기(지역2)
      if (K.aqueductFalls && K.seaY !== undefined) {
        const top = round(o.yAqueduct + K.aqueductFalls * sc);
        for (const along of [-12, 12]) {
          out.push({ kind: K.fall, x: round(c.x + Math.cos(rot) * along * sc), z: round(c.z - Math.sin(rot) * along * sc), rot: round(rot + Math.PI / 2), scale: round(0.8 * sc), y: top, len: round(top - K.seaY) });
        }
      }
    }
  });
  return out;
}

/** 원 둘레에 같은 세트피스를 고르게 세운다(정면 +z가 원 바깥을 보게 회전) */
function ringSets(kind: SetPieceDef['kind'], cx: number, cz: number, radius: number, count: number, phase: number, scale = 1, y = 0, spin = 0): SetPieceDef[] {
  const out: SetPieceDef[] = [];
  for (let i = 0; i < count; i++) {
    const a = phase + (i / count) * Math.PI * 2;
    // 입구(남쪽, +z) 쪽 8도 안은 비운다(다리가 닿는 자리)
    const dx = Math.sin(a), dz = Math.cos(a);
    if (dz > 0.985) continue;
    // spin(지역2 기둥 벽): 같은 조각이 같은 면만 보이지 않게 개체마다 정해진 만큼 더 돌리고 크기를 ±15% 흔든다(황금각 수열, 결정적)
    const g = ((i + 1) * 0.618034) % 1;
    out.push({ kind, x: Math.round((cx + dx * radius) * 100) / 100, z: Math.round((cz + dz * radius) * 100) / 100, rot: spin ? Math.round((Math.atan2(dx, dz) + spin * g * Math.PI * 2) * 1000) / 1000 : Math.atan2(dx, dz), scale: spin ? Math.round(scale * (0.85 + 0.3 * ((g * 7.3) % 1)) * 100) / 100 : scale, ...(y ? { y } : {}) });
  }
  return out;
}

// ═══ A — 절벽 테라스 유적 ═══════════════════════════════════════════
const A_ROOM1 = union(subtract(chamfer(r(-12, -42, 12, -12), 6, ['ne']), r(7, -32, 12, -26)), r(-20, -36, -12, -22));
const A_ROOM2 = union(chamfer(r(-10, -92, 10, -56), 5, ['ne', 'sw']), r(-18, -80, -10, -62));
const A_ROOM3 = subtract(chamfer(r(-62, -96, -32, -54), 5, ['nw', 'se']), r(-52, -84, -42, -70));
const A_ROOM4 = ellipse(-47, -130.5, 22, 20.5);
const A_FLOORS: Rect[] = union(
  r(-4, -12, 4, 4), // 입구 단
  A_ROOM1, r(-3, -56, 3, -42), // 방1, 다리 1
  A_ROOM2, r(-32, -74, -18, -68), // 방2, 서쪽 다리
  A_ROOM3, r(-50, -110, -44, -96), // 방3, 북쪽 다리
  A_ROOM4,
);
const A_ROOMS: RoomSpec[] = [
  { name: '1-1 무너진 회랑', kind: 'battle', floor: A_ROOM1, area: r(-20, -42, 12, -12), gateIn: 0, gateOut: 1, waves: WAVES_1 },
  { name: '1-2 이끼 정원', kind: 'battle', floor: A_ROOM2, area: r(-18, -92, 10, -56), gateIn: 2, gateOut: 3, waves: WAVES_2 },
  { name: '1-3 구멍 난 테라스', kind: 'battle', floor: A_ROOM3, area: r(-62, -96, -32, -54), gateIn: 4, gateOut: 5, waves: WAVES_3 },
  { name: '1-보스 뿌리 아레나', kind: 'boss', floor: A_ROOM4, area: r(-69, -151, -25, -110), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: -47, z: -138 } },
];
const A_LM: Landmark[] = [
    { kind: 'pillar', rect: r(-10, -19, -8, -17), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(7, -19, 9, -17), height: 4.5, solid: true },
    { kind: 'rubble', rect: r(-17, -31, -14, -28), height: 1.2, solid: true },
    { kind: 'pillar', rect: r(-9, -40, -7, -38), height: 4.5, solid: true },
    { kind: 'root', rect: r(2, -39, 6, -36), height: 2.4, solid: true },
    { kind: 'pillar', rect: r(-8, -60, -6, -58), height: 4.5, solid: true },
    { kind: 'rubble', rect: r(4, -70, 7, -67), height: 1.2, solid: true },
    { kind: 'pillar', rect: r(5, -88, 7, -86), height: 4.5, solid: true },
    { kind: 'root', rect: r(-17, -78, -13, -75), height: 2.4, solid: true },
    { kind: 'pillar', rect: r(-60, -92, -58, -90), height: 5, solid: true },
    { kind: 'pillar', rect: r(-37, -60, -35, -58), height: 5, solid: true },
    { kind: 'rubble', rect: r(-58, -62, -55, -59), height: 1.2, solid: true },
    { kind: 'root', rect: r(-40, -93, -36, -90), height: 2.4, solid: true },
    { kind: 'pillar', rect: r(-62, -138, -60, -136), height: 6, solid: true },
    { kind: 'pillar', rect: r(-34, -138, -32, -136), height: 6, solid: true },
    { kind: 'pillar', rect: r(-62, -124, -60, -122), height: 6, solid: true },
    { kind: 'pillar', rect: r(-34, -124, -32, -122), height: 6, solid: true },
];
const A_RAW: MapDef = {
  id: 'r1',
  stageKey: 'a',
  name: '절벽 테라스 유적',
  floors: A_FLOORS,
  landmarks: A_LM,
  gates: [
    { rect: r(-4, -12, 4, -11), openAtStart: true }, // 0: 방1 입구
    { rect: r(-3, -43, 3, -42), openAtStart: false }, // 1: 방1 출구(다리 1)
    { rect: r(-3, -56, 3, -55), openAtStart: true }, // 2: 방2 입구
    { rect: r(-19, -74, -18, -68), openAtStart: false }, // 3: 방2 출구(서쪽 다리)
    { rect: r(-32, -74, -31, -68), openAtStart: true }, // 4: 방3 입구
    { rect: r(-50, -97, -44, -96), openAtStart: false }, // 5: 방3 출구(북쪽 다리)
    { rect: r(-50, -110, -44, -109), openAtStart: true }, // 6: 아레나 입구
  ],
  rooms: A_ROOMS.map((rm) => roomOf(rm, A_LM.filter((l) => l.solid).map((l) => l.rect))),
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 3.2,
  edgeStyle: 'cliff',
  edgeZones: [
    // 다리(아치 다리 세트피스가 자기 난간을 가진다)
    { rect: r(-5, -55, 5, -43), style: 'bare' }, { rect: r(-31, -76, -19, -66), style: 'bare' }, { rect: r(-52, -109, -42, -97), style: 'bare' },
    // 방1·방2 남쪽 가장자리는 낮은 난간(무너진 담)이 이어진다
    { rect: r(-14, -22, 14, -12), style: 'parapet' },
    { rect: r(-12, -66, 12, -56), style: 'parapet' },
    // 아레나 둘레는 큰 뿌리가 바닥을 감싼다
    { rect: r(-72, -152, -22, -112), style: 'roots' },
  ],
  sets: [
    { kind: 'bridge', x: 0, z: -49, rot: 0, len: 12 }, { kind: 'bridge', x: -25, z: -71, rot: Math.PI / 2, len: 12 }, { kind: 'bridge', x: -47, z: -103, rot: 0, len: 12 },
    { kind: 'ruin_arch', x: 0, z: -12.5, rot: 0 }, { kind: 'ruin_arch', x: -47, z: -110.5, rot: 0 },
    ...farSets(A_FLOORS, 11, { cliffs: 16, falls: 7, aqueducts: 3, minD: 46, maxD: 92, sep: 30, yCliff: -33, yAqueduct: -14 }),
  ],
};

// ═══ B — 수로 광장(동 → 서) ═════════════════════════════════════════
const B_ROOM1 = union(r(-56, -4, -12, 6), r(-44, -8, -28, -4), r(-52, 6, -36, 12));
const B_ROOM2 = disk(-90, 0, 18);
const B_ROOM3 = subtract(r(-172, -12, -124, 12), r(-160, -4, -136, 4));
const B_ROOM4 = disk(-207, 0, 19);
const B_FLOORS: Rect[] = union(
  r(-12, -4, 4, 4), // 입구 단
  B_ROOM1, r(-72, -3, -56, 3), // 방1, 다리 1
  B_ROOM2, r(-124, -3, -108, 3), // 방2(원형 광장 1), 다리 2
  B_ROOM3, r(-188, -3, -172, 3), // 방3, 다리 3
  B_ROOM4, // 원형 광장 2(아레나)
);
const B_ROOMS: RoomSpec[] = [
  { name: '1-1 물길 옆 좁은 길', kind: 'battle', floor: B_ROOM1, area: r(-56, -8, -12, 12), gateIn: 0, gateOut: 1, waves: WAVES_B1 },
  { name: '1-2 첫 번째 광장', kind: 'battle', floor: B_ROOM2, area: r(-108, -18, -72, 18), gateIn: 2, gateOut: 3, waves: WAVES_B2 },
  { name: '1-3 못을 낀 갈림길', kind: 'battle', floor: B_ROOM3, area: r(-172, -12, -124, 12), gateIn: 4, gateOut: 5, waves: WAVES_B3 },
  { name: '1-보스 두 번째 광장', kind: 'boss', floor: B_ROOM4, area: r(-226, -19, -188, 19), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: -214, z: 0 } },
];
const B_LM: Landmark[] = [
    { kind: 'pillar', rect: r(-50, -4, -48, -2), height: 4.5, solid: true },
    { kind: 'rubble', rect: r(-36, 2, -33, 5), height: 1.2, solid: true },
    { kind: 'pillar', rect: r(-22, -3, -20, -1), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-100, -12, -98, -10), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-82, 10, -80, 12), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-100, 10, -98, 12), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-82, -12, -80, -10), height: 4.5, solid: true },
    { kind: 'rubble', rect: r(-152, -10, -149, -7), height: 1.2, solid: true },
    { kind: 'rubble', rect: r(-146, 7, -143, 10), height: 1.2, solid: true },
    { kind: 'pillar', rect: r(-168, -3, -166, -1), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-130, 2, -128, 4), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(-214, -13, -212, -11), height: 5.5, solid: true },
    { kind: 'pillar', rect: r(-214, 11, -212, 13), height: 5.5, solid: true },
    { kind: 'pillar', rect: r(-200, -13, -198, -11), height: 5.5, solid: true },
    { kind: 'pillar', rect: r(-200, 11, -198, 13), height: 5.5, solid: true },
];
const B_RAW: MapDef = {
  id: 'r1',
  stageKey: 'b',
  name: '수로 광장',
  floors: B_FLOORS,
  landmarks: B_LM,
  gates: [
    { rect: r(-12, -3, -11, 3), openAtStart: true }, // 0: 방1 입구
    { rect: r(-57, -3, -56, 3), openAtStart: false }, // 1: 방1 출구(다리 1)
    { rect: r(-72, -3, -71, 3), openAtStart: true }, // 2: 방2 입구
    { rect: r(-109, -3, -108, 3), openAtStart: false }, // 3: 방2 출구(다리 2)
    { rect: r(-124, -3, -123, 3), openAtStart: true }, // 4: 방3 입구
    { rect: r(-173, -3, -172, 3), openAtStart: false }, // 5: 방3 출구(다리 3)
    { rect: r(-188, -3, -187, 3), openAtStart: true }, // 6: 광장 2 입구
  ],
  rooms: B_ROOMS.map((rm) => roomOf(rm, B_LM.filter((l) => l.solid).map((l) => l.rect))),
  exits: [],
  spawn: { x: 0, z: 0, facing: W },
  wallHeight: 3.0,
  edgeStyle: 'water',
  // 수로 바깥은 전부 얕은 물(수면 -0.9m). 남쪽 가장자리만 낮은 난간 둑으로 둘러 읽힌다
  water: [r(-260, -60, 20, 60)],
  edgeZones: [
    { rect: r(-71, -5, -57, 5), style: 'bare' }, { rect: r(-123, -5, -109, 5), style: 'bare' }, { rect: r(-187, -5, -173, 5), style: 'bare' },
    { rect: r(-60, 6, -8, 14), style: 'parapet' },
  ],
  sets: [
    { kind: 'bridge', x: -64, z: 0, rot: Math.PI / 2, len: 14 }, { kind: 'bridge', x: -116, z: 0, rot: Math.PI / 2, len: 14 }, { kind: 'bridge', x: -180, z: 0, rot: Math.PI / 2, len: 14 },
    { kind: 'ruin_arch', x: -12.5, z: 0, rot: Math.PI / 2 }, { kind: 'ruin_arch', x: -187.5, z: 0, rot: Math.PI / 2 },
    ...farSets(B_FLOORS, 23, { cliffs: 18, falls: 6, aqueducts: 6, minD: 40, maxD: 80, sep: 34, yCliff: -33, yAqueduct: -4.5 }),
  ],
};

// ═══ M — 고블린 전쟁 캠프(중간보스 아레나) ═══════════════════════════
const M_ROOM1 = chamfer(r(-14, -40, 14, -12), 5, ['ne', 'nw']);
const M_ROOM2 = chamfer(r(-18, -84, 18, -52), 5, ['se', 'sw']);
const M_ROOM3 = chamfer(r(-20, -130, 20, -96), 6, ['ne', 'nw']);
const M_ROOM4 = disk(0, -163, 21);
const M_FLOORS: Rect[] = union(
  r(-4, -12, 4, 4),
  M_ROOM1, r(-3, -52, 3, -40),
  M_ROOM2, r(-3, -96, 3, -84),
  M_ROOM3, r(-3, -142, 3, -130),
  M_ROOM4,
);
const M_ROOMS: RoomSpec[] = [
  { name: '1-1 목책 마당', kind: 'battle', floor: M_ROOM1, area: r(-14, -40, 14, -12), gateIn: 0, gateOut: 1, waves: WAVES_1 },
  { name: '1-2 바위 갈림길', kind: 'battle', floor: M_ROOM2, area: r(-18, -84, 18, -52), gateIn: 2, gateOut: 3, waves: WAVES_2 },
  { name: '1-3 망루 문간', kind: 'battle', floor: M_ROOM3, area: r(-20, -130, 20, -96), gateIn: 4, gateOut: 5, waves: WAVES_3 },
  { name: '1-보스 전쟁 투기장', kind: 'boss', floor: M_ROOM4, area: r(-21, -184, 21, -142), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: 0, z: -172 }, boss: 'chieftain' },
];
const M_LM: Landmark[] = [
    { kind: 'bonfire', rect: r(-11, -20, -9, -18), height: 1.6, solid: true },
    { kind: 'bonfire', rect: r(9, -20, 11, -18), height: 1.6, solid: true },
    { kind: 'tower', rect: r(-11, -37, -7, -33), height: 7, solid: true },
    { kind: 'rubble', rect: r(8, -34, 11, -31), height: 1.2, solid: true },
    { kind: 'bonfire', rect: r(-15, -62, -13, -60), height: 1.6, solid: true },
    { kind: 'bonfire', rect: r(13, -76, 15, -74), height: 1.6, solid: true },
    { kind: 'rubble', rect: r(-4, -72, 4, -64), height: 3, solid: true },
    { kind: 'tower', rect: r(-17, -83, -13, -79), height: 7, solid: true },
    { kind: 'tower', rect: r(13, -83, 17, -79), height: 7, solid: true },
    { kind: 'tower', rect: r(-17, -126, -13, -122), height: 7.5, solid: true },
    { kind: 'tower', rect: r(13, -126, 17, -122), height: 7.5, solid: true },
    { kind: 'bonfire', rect: r(-9, -104, -7, -102), height: 1.6, solid: true },
    { kind: 'bonfire', rect: r(7, -104, 9, -102), height: 1.6, solid: true },
    { kind: 'rubble', rect: r(-8, -116, -5, -113), height: 1.2, solid: true },
    { kind: 'tower', rect: r(-19, -166, -15, -162), height: 8, solid: true },
    { kind: 'tower', rect: r(15, -166, 19, -162), height: 8, solid: true },
    { kind: 'throne', rect: r(-4, -182, 4, -177), height: 6, solid: true },
];
const M_RAW: MapDef = {
  id: 'r1',
  stageKey: 'm',
  name: '고블린 전쟁 캠프',
  floors: M_FLOORS,
  landmarks: M_LM,
  gates: [
    { rect: r(-4, -12, 4, -11), openAtStart: true },
    { rect: r(-3, -41, 3, -40), openAtStart: false },
    { rect: r(-3, -52, 3, -51), openAtStart: true },
    { rect: r(-3, -85, 3, -84), openAtStart: false },
    { rect: r(-3, -96, 3, -95), openAtStart: true },
    { rect: r(-3, -131, 3, -130), openAtStart: false },
    { rect: r(-3, -142, 3, -141), openAtStart: true },
  ],
  rooms: M_ROOMS.map((rm) => roomOf(rm, M_LM.filter((l) => l.solid).map((l) => l.rect))),
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 3.2,
  edgeStyle: 'palisade',
  edgeZones: [
    { rect: r(-5, -51, 5, -41), style: 'bare' }, { rect: r(-5, -95, 5, -85), style: 'bare' }, { rect: r(-5, -141, 5, -131), style: 'bare' },
  ],
  sets: [
    { kind: 'palisade_gate', x: 0, z: -12.5, rot: 0 }, { kind: 'palisade_gate', x: 0, z: -96.5, rot: 0 }, { kind: 'palisade_gate', x: 0, z: -142.5, rot: 0 },
    { kind: 'bridge_wood', x: 0, z: -46, rot: 0, len: 10 }, { kind: 'bridge_wood', x: 0, z: -90, rot: 0, len: 10 }, { kind: 'bridge_wood', x: 0, z: -136, rot: 0, len: 10 },
    // 투기장 안쪽 둘레(반지름 18.5): 모닥불 6 · 깃대 4 · 뼈 더미 3 — 목책 안쪽 가장자리를 따라 서므로 길을 막지 않는다
    ...ringSets('bonfire', 0, -163, 18.5, 6, 0.4), ...ringSets('banner_pole', 0, -163, 19.2, 4, 0.9), ...ringSets('bone_pile', 0, -163, 16.5, 3, 1.6),
    ...farSets(M_FLOORS, 37, { cliffs: 14, falls: 6, aqueducts: 0, minD: 44, maxD: 86, sep: 30, yCliff: -33, yAqueduct: -14 }),
  ],
};

// ═══ F — 고목 성소(지역보스 아레나) ═════════════════════════════════
const F_ROOM1 = ellipse(0, -28, 16, 16);
const F_ROOM2 = subtract(disk(0, -75, 17), disk(0, -75, 5));
const F_ROOM3 = ellipse(0, -123, 20, 17);
const F_ROOM4 = disk(0, -178, 24);
const F_FLOORS: Rect[] = union(
  r(-4, -12, 4, 4),
  F_ROOM1, r(-3, -58, 3, -44),
  F_ROOM2, r(-3, -106, 3, -92),
  F_ROOM3, r(-3, -154, 3, -140),
  F_ROOM4,
);
const F_ROOMS: RoomSpec[] = [
  { name: '1-1 뿌리 동굴', kind: 'battle', floor: F_ROOM1, area: r(-16, -44, 16, -12), gateIn: 0, gateOut: 1, waves: WAVES_1 },
  { name: '1-2 꺼진 뿌리 광장', kind: 'battle', floor: F_ROOM2, area: r(-17, -92, 17, -58), gateIn: 2, gateOut: 3, waves: WAVES_2 },
  { name: '1-3 고목의 그늘', kind: 'battle', floor: F_ROOM3, area: r(-20, -140, 20, -106), gateIn: 4, gateOut: 5, waves: WAVES_3 },
  { name: '1-보스 고목 성소', kind: 'boss', floor: F_ROOM4, area: r(-24, -202, 24, -154), gateIn: 6, gateOut: -1, waves: [], bossAt: { x: 0, z: -190 }, boss: 'treant' },
];
const F_LM: Landmark[] = [
    { kind: 'root', rect: r(-12, -22, -9, -19), height: 2.6, solid: true },
    { kind: 'root', rect: r(10, -34, 13, -31), height: 2.6, solid: true },
    { kind: 'pillar', rect: r(-9, -40, -7, -38), height: 4.5, solid: true },
    { kind: 'root', rect: r(-13, -70, -10, -67), height: 2.6, solid: true },
    { kind: 'root', rect: r(10, -82, 13, -79), height: 2.6, solid: true },
    { kind: 'pillar', rect: r(-7, -63, -5, -61), height: 4.5, solid: true },
    { kind: 'pillar', rect: r(5, -63, 7, -61), height: 4.5, solid: true },
    { kind: 'root', rect: r(-14, -126, -11, -123), height: 2.6, solid: true },
    { kind: 'root', rect: r(11, -118, 14, -115), height: 2.6, solid: true },
    { kind: 'pillar', rect: r(-12, -135, -10, -133), height: 5, solid: true },
    { kind: 'pillar', rect: r(10, -135, 12, -133), height: 5, solid: true },
    { kind: 'pillar', rect: r(-19, -172, -17, -170), height: 6, solid: true },
    { kind: 'pillar', rect: r(17, -172, 19, -170), height: 6, solid: true },
    { kind: 'pillar', rect: r(-13, -194, -11, -192), height: 6.5, solid: true },
    { kind: 'pillar', rect: r(11, -194, 13, -192), height: 6.5, solid: true },
];
const F_RAW: MapDef = {
  id: 'r1',
  stageKey: 'f',
  name: '고목 성소',
  floors: F_FLOORS,
  landmarks: F_LM,
  gates: [
    { rect: r(-4, -12, 4, -11), openAtStart: true },
    { rect: r(-3, -45, 3, -44), openAtStart: false },
    { rect: r(-3, -58, 3, -57), openAtStart: true },
    { rect: r(-3, -93, 3, -92), openAtStart: false },
    { rect: r(-3, -106, 3, -105), openAtStart: true },
    { rect: r(-3, -141, 3, -140), openAtStart: false },
    { rect: r(-3, -154, 3, -153), openAtStart: true },
  ],
  rooms: F_ROOMS.map((rm) => roomOf(rm, F_LM.filter((l) => l.solid).map((l) => l.rect))),
  exits: [],
  spawn: { x: 0, z: 0, facing: N },
  wallHeight: 3.2,
  edgeStyle: 'roots',
  edgeZones: [
    { rect: r(-5, -57, 5, -45), style: 'bare' }, { rect: r(-5, -105, 5, -93), style: 'bare' }, { rect: r(-5, -153, 5, -141), style: 'bare' },
  ],
  sets: [
    { kind: 'root_gate', x: 0, z: -12.5, rot: 0 }, { kind: 'root_gate', x: 0, z: -58.5, rot: 0 }, { kind: 'root_gate', x: 0, z: -106.5, rot: 0 }, { kind: 'root_gate', x: 0, z: -154.5, rot: 0 },
    { kind: 'bridge_root', x: 0, z: -51, rot: 0, len: 12 }, { kind: 'bridge_root', x: 0, z: -99, rot: 0, len: 12 }, { kind: 'bridge_root', x: 0, z: -147, rot: 0, len: 12 },
    // 큰 뿌리 덩이가 원형 판 둘레를 감싼다(반지름 24 바깥쪽)
    ...ringSets('root_arc', 0, -178, 24.8, 9, 0.2, 1.0, -0.6), ...ringSets('root_arc', 0, -123, 20.8, 5, 0.4, 0.9, -0.6), ...ringSets('root_arc', 0, -75, 17.8, 5, 0.7, 0.8, -0.6),
    { kind: 'ruin_arch', x: -17, z: -190, rot: 0.5 }, { kind: 'ruin_arch', x: 17, z: -190, rot: -0.5 },
    ...farSets(F_FLOORS, 41, { cliffs: 16, falls: 8, aqueducts: 0, minD: 44, maxD: 88, sep: 30, yCliff: -33, yAqueduct: -14 }),
  ],
};

// ═══ 문틀 ═══════════════════════════════════════════════════════════
/** 모든 문(입구·출구 일곱 곳) 가운데에 문틀 세트피스를 놓는다. 손으로 놓은 같은 종류 문틀은 걷어 낸다(문 위치와 어긋나지 않게). 문틀 폭은 문 칸 폭에 맞춰 늘이고 줄인다 */
function withGateFrames(m: MapDef, kind: SetPieceKind, frameW: number): MapDef {
  const frames: SetPieceDef[] = m.gates.map((g) => {
    const w = g.rect.x1 - g.rect.x0, d = g.rect.z1 - g.rect.z0, horizontal = w >= d, span = Math.max(w, d);
    return { kind, x: (g.rect.x0 + g.rect.x1) / 2, z: (g.rect.z0 + g.rect.z1) / 2, rot: horizontal ? 0 : Math.PI / 2, scale: Math.min(1.25, Math.max(0.85, span / frameW)) };
  });
  return { ...m, sets: [...frames, ...(m.sets ?? []).filter((x) => x.kind !== kind)] };
}
const A_BASE = withGateFrames(A_RAW, 'ruin_arch', 6);
const B_BASE = withGateFrames(B_RAW, 'ruin_arch', 6);
const M_BASE = withGateFrames(M_RAW, 'palisade_gate', 6.4);
const F_BASE = withGateFrames(F_RAW, 'root_gate', 5.6);


const R1_A: MapDef = A_BASE;
const R1_A2: MapDef = mirrorMap(A_BASE, 'a2', { timeOfDay: 'dusk', name: '절벽 테라스 유적(해질녘)' });
const R1_B: MapDef = B_BASE;
const R1_B2: MapDef = mirrorMap(B_BASE, 'b2', { timeOfDay: 'morning', name: '수로 광장(아침 안개)' });
const R1_M: MapDef = M_BASE;
const R1_F: MapDef = F_BASE;

/** 지역1 스테이지 지도 6장(키 → 지도) */
const R1_STAGE_MAPS: Readonly<Record<StageMapKey, MapDef>> = { a: R1_A, a2: R1_A2, b: R1_B, b2: R1_B2, m: R1_M, f: R1_F };

// ═══════════════════════════════════════════════════════════════════
// 지역2(잿불 협곡) 스테이지 지도 4장 + 좌우 반전 2장(S6, docs/STAGE-WORLD-PRD.md 2.2·8절 S6). 스테이지 2-1~2-10이 6가지 판으로 보인다.
//   A  현무암 능선 — 기존 R2 지도 개량: 능선 → 용암 골을 다리 둘로 건너는 두 단 → 북서 능선 → 분화구 타원 → 북동 능선 → 팔각 고원 아레나 (2-1·2-2)
//   A′ A 좌우 반전 + 해질녘(붉은 재 노을)                                                                               (2-3·2-4)
//   M  용암 골렘 화로 — 팔각 슬래그 마당 → 십자 모루 전당 → 원형 단조장(화로 탑 고리·쇠사슬·거대 도가니)                     (2-5)
//   B  용암 강 다리 — 동쪽으로 섬을 잇는 다리: 섬 → 교각 둘을 꺾는 지그재그 다리 → 분화구 섬 → 북쪽 다리 → 원형 섬 아레나 (2-6·2-7)
//   B′ B 좌우 반전(서쪽으로) + 잿빛 새벽                                                                                (2-8·2-9)
//   F  대주술사 제단 — 재 계단 타원 → 가운데가 용암 구덩이인 불의 고리 → 용암 해자에 둘러싼 원형 제단(오벨리스크·불의 제단)  (2-10)
// 방은 기존 R2와 같이 전투방 2 + 아레나 1(웨이브·후열 주술사·용암 웅덩이·분출구 규칙 그대로)이라 지역2 수입·시간 표(sim)가 바뀌지 않는다.
// 빈칸 아래는 모두 용암 바다(seaY, 렌더 전용)다: A 깊은 협곡(-14) · M 화로 아래(-10) · B 흐르는 강(-4.5) · F 해자(-2.6).

/** R2 방1·방2 웨이브(levels.R2와 같다): 근접 무리 + 후열 주술사 → 방패 벽 + 주술사 증원 / 멧돼지 돌진 → 포격진 → 혼합 정예 */
const E_WAVES_1: readonly WaveDef[] = [{ counts: [10, 0, 0, 2] }, { counts: [8, 4, 0, 3] }];
const E_WAVES_2: readonly WaveDef[] = [{ counts: [8, 0, 3, 0] }, { counts: [0, 4, 0, 4] }, { counts: [12, 2, 2, 2], elites: 1 }];
const E_WAVES_B1: readonly WaveDef[] = [{ counts: [10, 2, 0, 2] }, { counts: [6, 4, 2, 3] }];
const E_WAVES_B2: readonly WaveDef[] = [{ counts: [6, 0, 4, 1] }, { counts: [2, 4, 0, 4] }, { counts: [12, 2, 2, 2], elites: 1 }];

/** 사각형 목록을 감싸는 사각형 */
function bboxOf(rects: readonly Rect[]): Rect {
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (const q of rects) { x0 = Math.min(x0, q.x0); z0 = Math.min(z0, q.z0); x1 = Math.max(x1, q.x1); z1 = Math.max(z1, q.z1); }
  return r(x0, z0, x1, z1);
}
/** 원 둘레 자리(남쪽 입구 쪽 skip 라디안 안은 비운다). a = 0 → 남쪽(+z) */
function ringPoints(cx: number, cz: number, radius: number, count: number, phase: number, skip: number): { x: number; z: number }[] {
  const out: { x: number; z: number }[] = [];
  for (let i = 0; i < count; i++) {
    const a = phase + (i / count) * Math.PI * 2;
    if (Math.abs(Math.atan2(Math.sin(a), Math.cos(a))) < skip) continue;
    out.push({ x: Math.round(cx + Math.sin(a) * radius), z: Math.round(cz + Math.cos(a) * radius) });
  }
  return out;
}
/** 원 둘레에 size×size 랜드마크를 고르게 세운다 */
function ringLandmarks(kind: Landmark['kind'], cx: number, cz: number, radius: number, count: number, phase: number, height: number, skip = 0.5, size = 2): Landmark[] {
  const h = size / 2;
  return ringPoints(cx, cz, radius, count, phase, skip).map((p) => ({ kind, rect: r(p.x - h, p.z - h, p.x + h, p.z + h), height, solid: true }));
}
/** 원 둘레 탑(ringLandmarks와 같은 자리) 사이를 잇는 쇠사슬(높이 y). 입구 쪽 틈은 잇지 않는다 */
function ringChains(cx: number, cz: number, radius: number, count: number, phase: number, skip: number, y: number): SetPieceDef[] {
  const pts = ringPoints(cx, cz, radius, count, phase, skip);
  const out: SetPieceDef[] = [];
  for (let i = 0; i + 1 < pts.length; i++) out.push(chainBetween(pts[i], pts[i + 1], y));
  return out;
}
/** 두 점(탑 꼭대기) 사이 쇠사슬. 사슬 모델은 x 방향 10.4m: 회전 −atan2(dz, dx), 균일 배율로 길이를 맞춘다(처짐도 같이 커진다) */
function chainBetween(p: { x: number; z: number }, q: { x: number; z: number }, y: number): SetPieceDef {
  const dx = q.x - p.x, dz = q.z - p.z, len = Math.hypot(dx, dz);
  return { kind: 'e_chain_span', x: (p.x + q.x) / 2, z: (p.z + q.z) / 2, rot: Math.round(-Math.atan2(dz, dx) * 1000) / 1000, scale: Math.round((len / 10.4) * 100) / 100, y };
}
/** 지역2 방 만들기: 웨이브 입구·대표 지점은 랜드마크와 용암 웅덩이를 피한다 */
function emberRooms(specs: readonly RoomSpec[], lm: readonly Landmark[], lava: readonly Rect[]): RoomDef[] {
  const avoid = [...lm.filter((l) => l.solid).map((l) => l.rect), ...lava];
  return specs.map((s) => roomOf(s, avoid));
}
/** 지역2 공통 지도 값: 지도 id r2, 위험(용암 웅덩이 화상·분출구), 예약표, 현무암 기둥 가장자리 */
const EMBER_BASE = { id: 'r2' as const, exits: [] as ExitDef[], wallHeight: 4.2, hazardTheme: 'ember' as const, threatClock: true, edgeStyle: 'basalt' as const };
/** 지역2 원경: 용암 바다에서 솟은 현무암 첨탑(일부는 꼭대기에서 용암이 흘러내린다) + 용암이 떨어지는 아치 돌다리 */
function emberFar(floors: readonly Rect[], seed: number, seaY: number, cliffs: number, falls: number, aqueducts: number, minD = 40, maxD = 84): SetPieceDef[] {
  return farSets(floors, seed, {
    cliffs, falls, aqueducts, minD, maxD, sep: 26, yCliff: seaY - 1, yAqueduct: seaY - 0.5,
    kinds: { cliff: 'e_far_spire', fall: 'lavafall', aqueduct: 'e_far_aqueduct', fallTop: 22, aqueductFalls: 11, seaY },
  });
}

// ═══ 지역2 A — 현무암 능선(기존 R2 개량) ═════════════════════════════
const EA_SEA = -14;
const EA_ROOM1 = union(
  chamfer(r(-19, -24, 19, -14), 5, ['sw', 'se']), // 남쪽 단
  r(-13, -30, -9, -24), r(9, -30, 13, -24), // 용암 골을 건너는 다리 둘(폭 4)
  subtract(chamfer(r(-19, -43, 19, -30), 6, ['ne']), disk(19, -37, 3)), // 북쪽 단(후열)
);
const EA_ROOM2 = union(subtract(ellipse(-8, -73, 17, 18), disk(-25, -79, 4), disk(9, -65, 4)), r(-16, -59, -12, -55), r(2, -92, 6, -83));
const EA_ARENA = subtract(chamfer(r(-14, -141, 22, -105), 9, ['nw', 'ne', 'sw', 'se']), disk(-14, -123, 3.5), disk(22, -123, 3.5));
const EA_LAVA: Rect[] = [r(-11, -76, -5, -70), r(-9, -117, -5, -113), r(13, -117, 17, -113)];
const EA_FLOORS: Rect[] = union(
  r(-5, -4, 5, 5), r(-2, -14, 2, -4), // 입구 바위턱 · 능선 1
  EA_ROOM1, r(-16, -55, -12, -43), // 방1 · 능선 2(북서)
  EA_ROOM2, r(2, -105, 6, -92), // 방2 · 능선 3(북동)
  EA_ARENA,
);
const EA_LM: Landmark[] = [
  // 방1: 다리목 양옆 화로 탑 넷(다리 머리를 지키는 실루엣)
  { kind: 'tower', rect: r(-17, -23, -15, -21), height: 6, solid: true }, { kind: 'tower', rect: r(15, -23, 17, -21), height: 6, solid: true },
  { kind: 'tower', rect: r(-17, -34, -15, -32), height: 6, solid: true }, { kind: 'tower', rect: r(15, -34, 17, -32), height: 6, solid: true },
  { kind: 'basalt', rect: r(-6, -38, -4, -36), height: 4, solid: true }, { kind: 'basalt', rect: r(4, -38, 6, -36), height: 4, solid: true },
  // 방2: 분화구 둘레 현무암 기둥, 북쪽 잔해
  { kind: 'basalt', rect: r(-22, -67, -20, -65), height: 5, solid: true }, { kind: 'basalt', rect: r(3, -67, 5, -65), height: 5, solid: true },
  { kind: 'rubble', rect: r(-5, -83, -2, -81), height: 1.2, solid: true },
  // 아레나: 북쪽 모서리 기둥 둘 + 남쪽 화로 둘
  { kind: 'basalt', rect: r(-10, -136, -8, -134), height: 7, solid: true }, { kind: 'basalt', rect: r(16, -136, 18, -134), height: 7, solid: true },
  { kind: 'bonfire', rect: r(-6, -109, -4, -107), height: 1.4, solid: true }, { kind: 'bonfire', rect: r(12, -109, 14, -107), height: 1.4, solid: true },
];
const EA_ROOMS: RoomSpec[] = [
  { name: '2-1 잿빛 다리목', kind: 'battle', floor: EA_ROOM1, area: r(-19, -43, 19, -14), gateIn: 0, gateOut: 1, waves: E_WAVES_1, backline: r(-17, -42, 17, -32) },
  { name: '2-2 분화구 계단', kind: 'battle', floor: EA_ROOM2, area: r(-25, -92, 9, -55), gateIn: 2, gateOut: 3, waves: E_WAVES_2, backline: r(-18, -90, 2, -82) },
  { name: '2-아레나 현무암 고원', kind: 'boss', floor: EA_ARENA, area: r(-14, -141, 22, -105), gateIn: 4, gateOut: -1, waves: [], bossAt: { x: 4, z: -129 }, boss: 'ember' },
];
const EA_RAW: MapDef = {
  ...EMBER_BASE,
  stageKey: 'a',
  name: '현무암 능선',
  floors: EA_FLOORS,
  landmarks: EA_LM,
  gates: [
    { rect: r(-2, -14, 2, -13), openAtStart: true }, // 0: 방1 입구(능선 1 끝)
    { rect: r(-16, -44, -12, -43), openAtStart: false }, // 1: 방1 출구(능선 2 시작)
    { rect: r(-16, -55, -12, -54), openAtStart: true }, // 2: 방2 입구
    { rect: r(2, -93, 6, -92), openAtStart: false }, // 3: 방2 출구(능선 3 시작)
    { rect: r(2, -105, 6, -104), openAtStart: true }, // 4: 아레나 입구
  ],
  rooms: emberRooms(EA_ROOMS, EA_LM, EA_LAVA),
  spawn: { x: 0, z: 0, facing: N },
  lava: EA_LAVA,
  vents: [
    // 방1: 다리 남쪽 머리. 반 주기 엇갈려 한쪽 다리는 늘 건널 수 있다
    { x: -11, z: -21.5, radius: 2.2, room: 0, offset: 0 }, { x: 11, z: -21.5, radius: 2.2, room: 0, offset: 0.5 },
    // 방2: 분화구 둘레 셋
    { x: -8, z: -65.5, radius: 2.4, room: 1, offset: 0 }, { x: -15.5, z: -73, radius: 2.4, room: 1, offset: 1 / 3 }, { x: -0.5, z: -73, radius: 2.4, room: 1, offset: 2 / 3 },
    // 아레나: 리더 체력 60% 이하
    { x: -4, z: -119, radius: 2.6, room: 2, offset: 0, fromBossHp: 0.6 }, { x: 12, z: -131, radius: 2.6, room: 2, offset: 0.25, fromBossHp: 0.6 },
    { x: 12, z: -119, radius: 2.6, room: 2, offset: 0.5, fromBossHp: 0.6 }, { x: -4, z: -131, radius: 2.6, room: 2, offset: 0.75, fromBossHp: 0.6 },
  ],
  seaY: EA_SEA,
  edgeZones: [
    { rect: r(-14, -30, -8, -24), style: 'bare' }, { rect: r(8, -30, 14, -24), style: 'bare' }, // 용암 골 다리
    { rect: r(-3, -14, 3, -4), style: 'chain' }, { rect: r(-17, -55, -11, -43), style: 'chain' }, { rect: r(1, -105, 7, -92), style: 'chain' }, // 능선 쇠사슬 난간
  ],
  sets: [
    { kind: 'e_bridge', x: -11, z: -27, rot: 0, len: 6 }, { kind: 'e_bridge', x: 11, z: -27, rot: 0, len: 6 },
    // 용암 골 양쪽 화로 탑 꼭대기를 잇는 쇠사슬(콘셉트: 다리목 탑 사이에 늘어진 사슬)
    chainBetween({ x: -16, z: -22 }, { x: -16, z: -33 }, 4.6), chainBetween({ x: 16, z: -22 }, { x: 16, z: -33 }, 4.6),
    { kind: 'e_banner', x: -4.5, z: -139.5, rot: 0 }, { kind: 'e_banner', x: 12.5, z: -139.5, rot: 0 }, { kind: 'e_banner', x: -18.5, z: -40.5, rot: 0 }, { kind: 'e_banner', x: 15.5, z: -17.5, rot: 0 },
    ...emberFar(EA_FLOORS, 52, EA_SEA, 18, 6, 3, 34, 82),
  ],
};

// ═══ 지역2 B — 용암 강 다리(동쪽으로 섬을 잇는다) ═══════════════════════
const EB_SEA = -4.5;
const EB_ROOM1 = subtract(disk(32, 0, 14), disk(30, 14, 5), disk(43, -11, 3));
const EB_ROOM2 = subtract(disk(90, -18, 14), disk(103, -6, 5), disk(78, -30, 4));
const EB_ARENA = disk(90, -66, 20);
const EB_LAVA: Rect[] = [r(29, -3, 34, 2), r(87, -21, 93, -15), r(79, -62, 83, -58), r(97, -62, 101, -58)];
const EB_FLOORS: Rect[] = union(
  ellipse(0, 0, 6, 5), r(6, -2, 18, 2), // 입구 섬 · 다리 1
  EB_ROOM1,
  r(46, -2, 57, 2), r(57, -3, 64, 3), r(59, -15, 63, -3), r(57, -21, 64, -15), r(64, -20, 76, -16), // 지그재그 다리(동 → 북 → 동, 교각 둘)
  EB_ROOM2, r(88, -46, 92, -32), // 분화구 섬 · 북쪽 다리
  EB_ARENA,
);
const EB_LM: Landmark[] = [
  { kind: 'basalt', rect: r(23, -9, 25, -7), height: 4.5, solid: true }, { kind: 'basalt', rect: r(38, 6, 40, 8), height: 4.5, solid: true },
  { kind: 'rubble', rect: r(23, 5, 26, 8), height: 1.2, solid: true },
  // 교각 모서리 화로 탑(다리가 꺾이는 자리 표시)
  { kind: 'tower', rect: r(62, 1, 64, 3), height: 6, solid: true }, { kind: 'tower', rect: r(57, -21, 59, -19), height: 6, solid: true },
  { kind: 'basalt', rect: r(82, -10, 84, -8), height: 5, solid: true }, { kind: 'basalt', rect: r(97, -28, 99, -26), height: 5, solid: true },
  { kind: 'rubble', rect: r(96, -12, 99, -9), height: 1.2, solid: true },
  ...ringLandmarks('tower', 90, -66, 16, 4, Math.PI / 4, 6, 0.3),
];
const EB_ROOMS: RoomSpec[] = [
  { name: '2-1 첫 번째 섬', kind: 'battle', floor: EB_ROOM1, area: bboxOf(EB_ROOM1), gateIn: 0, gateOut: 1, waves: E_WAVES_B1, backline: r(36, -12, 45, 10) },
  { name: '2-2 분화구 섬', kind: 'battle', floor: EB_ROOM2, area: bboxOf(EB_ROOM2), gateIn: 2, gateOut: 3, waves: E_WAVES_B2, backline: r(80, -31, 100, -25) },
  { name: '2-아레나 용암 강 섬', kind: 'boss', floor: EB_ARENA, area: bboxOf(EB_ARENA), gateIn: 4, gateOut: -1, waves: [], bossAt: { x: 90, z: -74 }, boss: 'ember' },
];
const EB_RAW: MapDef = {
  ...EMBER_BASE,
  stageKey: 'b',
  name: '용암 강 다리',
  floors: EB_FLOORS,
  landmarks: EB_LM,
  gates: [
    { rect: r(17, -2, 18, 2), openAtStart: true }, // 0: 섬 1 입구(다리 1 끝)
    { rect: r(46, -2, 47, 2), openAtStart: false }, // 1: 섬 1 출구(지그재그 다리 시작)
    { rect: r(75, -20, 76, -16), openAtStart: true }, // 2: 섬 2 입구
    { rect: r(88, -33, 92, -32), openAtStart: false }, // 3: 섬 2 출구(북쪽 다리)
    { rect: r(88, -46, 92, -45), openAtStart: true }, // 4: 아레나 입구
  ],
  rooms: emberRooms(EB_ROOMS, EB_LM, EB_LAVA),
  spawn: { x: 0, z: 0, facing: Math.PI / 2 },
  lava: EB_LAVA,
  vents: [
    { x: 26, z: -4, radius: 2.2, room: 0, offset: 0 }, { x: 37, z: 4, radius: 2.2, room: 0, offset: 0.5 },
    { x: 90, z: -9.5, radius: 2.4, room: 1, offset: 0 }, { x: 82, z: -18, radius: 2.4, room: 1, offset: 1 / 3 }, { x: 98, z: -18, radius: 2.4, room: 1, offset: 2 / 3 },
    { x: 82, z: -66, radius: 2.6, room: 2, offset: 0, fromBossHp: 0.6 }, { x: 98, z: -78, radius: 2.6, room: 2, offset: 0.25, fromBossHp: 0.6 },
    { x: 98, z: -66, radius: 2.6, room: 2, offset: 0.5, fromBossHp: 0.6 }, { x: 82, z: -78, radius: 2.6, room: 2, offset: 0.75, fromBossHp: 0.6 },
  ],
  seaY: EB_SEA,
  edgeZones: [
    { rect: r(6, -3, 18, 3), style: 'bare' }, { rect: r(46, -3, 57, 3), style: 'bare' }, { rect: r(58, -15, 64, -3), style: 'bare' }, { rect: r(64, -21, 76, -15), style: 'bare' }, { rect: r(87, -46, 93, -32), style: 'bare' },
    // 교각은 쇠사슬 난간
    { rect: r(56, -4, 65, 4), style: 'chain' }, { rect: r(56, -22, 65, -14), style: 'chain' },
  ],
  sets: [
    { kind: 'e_bridge', x: 12, z: 0, rot: Math.PI / 2, len: 12 }, { kind: 'e_bridge', x: 51.5, z: 0, rot: Math.PI / 2, len: 11 },
    { kind: 'e_bridge', x: 61, z: -9, rot: 0, len: 12 }, { kind: 'e_bridge', x: 70, z: -18, rot: Math.PI / 2, len: 12 }, { kind: 'e_bridge', x: 90, z: -39, rot: 0, len: 14 },
    { kind: 'e_banner', x: 19, z: 9, rot: Math.PI / 2 }, { kind: 'e_banner', x: 77.5, z: -9, rot: Math.PI / 2 }, { kind: 'e_banner', x: 75, z: -82, rot: 0.6 }, { kind: 'e_banner', x: 105, z: -82, rot: -0.6 },
    // 강 위 바위섬(낮은 첨탑): 다리 사이 물길에 솟은 현무암
    { kind: 'e_far_spire', x: 52, z: 14, rot: 0.4, scale: 0.42, y: EB_SEA - 2 }, { kind: 'e_far_spire', x: 74, z: -4, rot: 1.3, scale: 0.36, y: EB_SEA - 2 },
    { kind: 'e_far_spire', x: 50, z: -16, rot: 2.1, scale: 0.4, y: EB_SEA - 2 }, { kind: 'e_far_spire', x: 72, z: -34, rot: 0.9, scale: 0.38, y: EB_SEA - 2 },
    ...emberFar(EB_FLOORS, 61, EB_SEA, 16, 4, 5, 32, 78),
  ],
};

// ═══ 지역2 M — 용암 골렘 화로(중간보스 아레나) ════════════════════════
const EM_SEA = -10;
const EM_ROOM1 = chamfer(r(-16, -44, 16, -14), 7, ['nw', 'ne', 'sw', 'se']);
const EM_ROOM2 = union(r(-21, -81, 21, -67), r(-8, -92, 8, -56));
const EM_ARENA = disk(0, -126, 22);
const EM_LAVA: Rect[] = [r(-11, -32, -7, -27), r(7, -32, 11, -27), r(-19, -78, -15, -70), r(15, -78, 19, -70), r(-12, -120, -8, -116), r(8, -120, 12, -116)];
const EM_FLOORS: Rect[] = union(
  r(-5, -4, 5, 5), r(-2, -14, 2, -4),
  EM_ROOM1, r(-2, -56, 2, -44),
  EM_ROOM2, r(-2, -104, 2, -92),
  EM_ARENA,
);
const EM_LM: Landmark[] = [
  { kind: 'anvil', rect: r(-2, -40, 2, -37), height: 1.8, solid: true },
  { kind: 'rubble', rect: r(-14, -22, -11, -19), height: 1.2, solid: true }, { kind: 'rubble', rect: r(11, -40, 14, -37), height: 1.2, solid: true },
  { kind: 'bonfire', rect: r(-12, -38, -10, -36), height: 1.4, solid: true }, { kind: 'bonfire', rect: r(10, -22, 12, -20), height: 1.4, solid: true },
  // 모루 전당: 가운데 큰 모루, 십자 팔 모서리 화로 탑
  { kind: 'anvil', rect: r(-2, -76, 2, -72), height: 1.8, solid: true },
  { kind: 'tower', rect: r(-7, -91, -5, -89), height: 6, solid: true }, { kind: 'tower', rect: r(5, -91, 7, -89), height: 6, solid: true },
  { kind: 'tower', rect: r(-7, -59, -5, -57), height: 6, solid: true }, { kind: 'tower', rect: r(5, -59, 7, -57), height: 6, solid: true },
  // 단조장: 둘레 화로 탑(남쪽 입구 비움) + 북쪽 거대 도가니
  ...ringLandmarks('tower', 0, -126, 19, 8, Math.PI / 8, 6.5, 0.5),
  { kind: 'crucible', rect: r(-3, -147, 3, -142), height: 6.4, solid: true },
];
const EM_ROOMS: RoomSpec[] = [
  { name: '2-1 슬래그 마당', kind: 'battle', floor: EM_ROOM1, area: r(-16, -44, 16, -14), gateIn: 0, gateOut: 1, waves: E_WAVES_1, backline: r(-12, -43, 12, -38) },
  { name: '2-2 모루 전당', kind: 'battle', floor: EM_ROOM2, area: r(-21, -92, 21, -56), gateIn: 2, gateOut: 3, waves: E_WAVES_2, backline: r(-8, -91, 8, -84) },
  { name: '2-보스 용암 단조장', kind: 'boss', floor: EM_ARENA, area: r(-22, -148, 22, -104), gateIn: 4, gateOut: -1, waves: [], bossAt: { x: 0, z: -131 }, boss: 'golem' },
];
const EM_RAW: MapDef = {
  ...EMBER_BASE,
  stageKey: 'm',
  name: '용암 골렘 화로',
  floors: EM_FLOORS,
  landmarks: EM_LM,
  gates: [
    { rect: r(-2, -14, 2, -13), openAtStart: true },
    { rect: r(-2, -45, 2, -44), openAtStart: false },
    { rect: r(-2, -56, 2, -55), openAtStart: true },
    { rect: r(-2, -93, 2, -92), openAtStart: false },
    { rect: r(-2, -104, 2, -103), openAtStart: true },
  ],
  rooms: emberRooms(EM_ROOMS, EM_LM, EM_LAVA),
  spawn: { x: 0, z: 0, facing: N },
  lava: EM_LAVA,
  vents: [
    { x: -9, z: -22, radius: 2.2, room: 0, offset: 0 }, { x: 9, z: -36, radius: 2.2, room: 0, offset: 0.5 },
    { x: -12, z: -74, radius: 2.4, room: 1, offset: 0 }, { x: 12, z: -74, radius: 2.4, room: 1, offset: 1 / 3 }, { x: 0, z: -84, radius: 2.4, room: 1, offset: 2 / 3 },
    { x: -9, z: -126, radius: 2.6, room: 2, offset: 0, fromBossHp: 0.6 }, { x: 9, z: -136, radius: 2.6, room: 2, offset: 0.25, fromBossHp: 0.6 },
    { x: 9, z: -126, radius: 2.6, room: 2, offset: 0.5, fromBossHp: 0.6 }, { x: -9, z: -136, radius: 2.6, room: 2, offset: 0.75, fromBossHp: 0.6 },
  ],
  seaY: EM_SEA,
  edgeStyle: 'forge',
  edgeZones: [
    { rect: r(-3, -14, 3, -4), style: 'chain' }, { rect: r(-3, -56, 3, -44), style: 'bare' }, { rect: r(-3, -104, 3, -92), style: 'bare' },
  ],
  sets: [
    { kind: 'e_bridge', x: 0, z: -50, rot: 0, len: 12 }, { kind: 'e_bridge', x: 0, z: -98, rot: 0, len: 12 },
    // 단조장 하늘: 화로 탑 사이 쇠사슬(탑 꼭대기 높이), 도가니 앞 깃발
    ...ringChains(0, -126, 19, 8, Math.PI / 8, 0.5, 5.2),
    { kind: 'e_banner', x: -6.5, z: -145.5, rot: 0 }, { kind: 'e_banner', x: 6.5, z: -145.5, rot: 0 },
    { kind: 'e_banner', x: -15.5, z: -16.5, rot: 0 }, { kind: 'e_banner', x: 15.5, z: -16.5, rot: 0 },
    ...emberFar(EM_FLOORS, 73, EM_SEA, 16, 6, 2, 34, 80),
  ],
};

// ═══ 지역2 F — 대주술사 제단(지역보스 아레나) ══════════════════════════
const EF_SEA = -2.6;
const EF_ROOM1 = ellipse(0, -30, 17, 16);
const EF_ROOM2 = ring(0, -76, 6, 18);
const EF_ARENA = disk(0, -130, 24);
const EF_LAVA: Rect[] = [r(-10, -26, -6, -22), r(6, -26, 10, -22), r(-12, -128, -8, -124), r(8, -128, 12, -124)];
const EF_FLOORS: Rect[] = union(
  r(-5, -4, 5, 5), r(-2, -14, 2, -4),
  EF_ROOM1, r(-2, -58, 2, -46),
  EF_ROOM2, r(-2, -106, 2, -94),
  EF_ARENA,
);
const EF_LM: Landmark[] = [
  { kind: 'basalt', rect: r(-13, -36, -11, -34), height: 4.5, solid: true }, { kind: 'basalt', rect: r(11, -36, 13, -34), height: 4.5, solid: true },
  { kind: 'pillar', rect: r(-1, -42, 1, -40), height: 5, solid: true },
  { kind: 'pillar', rect: r(-13, -77, -11, -75), height: 5, solid: true }, { kind: 'pillar', rect: r(11, -77, 13, -75), height: 5, solid: true },
  { kind: 'rubble', rect: r(-9, -90, -6, -87), height: 1.2, solid: true }, { kind: 'rubble', rect: r(6, -65, 9, -62), height: 1.2, solid: true },
  // 제단: 둘레 오벨리스크(남쪽 입구 비움) + 북쪽 불의 제단
  ...ringLandmarks('pillar', 0, -130, 20, 10, Math.PI / 10, 5.4, 0.4),
  { kind: 'altar', rect: r(-4, -153, 4, -148), height: 6, solid: true },
];
const EF_ROOMS: RoomSpec[] = [
  { name: '2-1 재의 계단', kind: 'battle', floor: EF_ROOM1, area: bboxOf(EF_ROOM1), gateIn: 0, gateOut: 1, waves: E_WAVES_1, backline: r(-12, -45, 12, -38) },
  { name: '2-2 불의 고리', kind: 'battle', floor: EF_ROOM2, area: bboxOf(EF_ROOM2), gateIn: 2, gateOut: 3, waves: E_WAVES_2, backline: r(-14, -93, 14, -87) },
  { name: '2-보스 대주술사 제단', kind: 'boss', floor: EF_ARENA, area: bboxOf(EF_ARENA), gateIn: 4, gateOut: -1, waves: [], bossAt: { x: 0, z: -136 }, boss: 'ember' },
];
const EF_RAW: MapDef = {
  ...EMBER_BASE,
  stageKey: 'f',
  name: '대주술사 제단',
  floors: EF_FLOORS,
  landmarks: EF_LM,
  gates: [
    { rect: r(-2, -14, 2, -13), openAtStart: true },
    { rect: r(-2, -47, 2, -46), openAtStart: false },
    { rect: r(-2, -58, 2, -57), openAtStart: true },
    { rect: r(-2, -95, 2, -94), openAtStart: false },
    { rect: r(-2, -106, 2, -105), openAtStart: true },
  ],
  rooms: emberRooms(EF_ROOMS, EF_LM, EF_LAVA),
  spawn: { x: 0, z: 0, facing: N },
  lava: EF_LAVA,
  vents: [
    { x: -12, z: -28, radius: 2.2, room: 0, offset: 0 }, { x: 12, z: -28, radius: 2.2, room: 0, offset: 0.5 },
    { x: -12, z: -70, radius: 2.4, room: 1, offset: 0 }, { x: 12, z: -70, radius: 2.4, room: 1, offset: 1 / 3 }, { x: 0, z: -88, radius: 2.4, room: 1, offset: 2 / 3 },
    { x: -10, z: -136, radius: 2.6, room: 2, offset: 0, fromBossHp: 0.6 }, { x: 10, z: -136, radius: 2.6, room: 2, offset: 0.25, fromBossHp: 0.6 },
    { x: 10, z: -120, radius: 2.6, room: 2, offset: 0.5, fromBossHp: 0.6 }, { x: -10, z: -120, radius: 2.6, room: 2, offset: 0.75, fromBossHp: 0.6 },
  ],
  seaY: EF_SEA,
  edgeZones: [
    { rect: r(-3, -58, 3, -46), style: 'bare' }, { rect: r(-3, -106, 3, -94), style: 'bare' }, { rect: r(-3, -14, 3, -4), style: 'chain' },
  ],
  sets: [
    { kind: 'e_bridge', x: 0, z: -52, rot: 0, len: 12 }, { kind: 'e_bridge', x: 0, z: -100, rot: 0, len: 12 },
    // 해자 바깥 가장자리: 용암 해자를 둘러싼 현무암 기둥 벽(제단을 사발처럼 감싼다)
    ...ringSets('e_basalt', 0, -130, 33, 22, 0.12, 2.4, EF_SEA - 1.5, 1), ...ringSets('e_basalt', 0, -130, 38, 16, 0.3, 3.4, EF_SEA - 2, 1),
    ...ringSets('e_basalt', 0, -76, 26, 12, 0.2, 2.0, EF_SEA - 1.5, 1),
    { kind: 'e_banner', x: -7.5, z: -150.5, rot: 0 }, { kind: 'e_banner', x: 7.5, z: -150.5, rot: 0 },
    ...emberFar(EF_FLOORS, 89, EF_SEA, 14, 6, 2, 46, 90),
  ],
};

// ═══ 지역2 문틀·반전 ════════════════════════════════════════════════
const EA_BASE = withGateFrames(EA_RAW, 'e_gate', 4.6);
const EB_BASE = withGateFrames(EB_RAW, 'e_gate', 4.6);
const EM_BASE = withGateFrames(EM_RAW, 'e_gate', 4.6);
const EF_BASE = withGateFrames(EF_RAW, 'e_gate', 4.6);
const R2_A: MapDef = EA_BASE;
const R2_A2: MapDef = mirrorMap(EA_BASE, 'a2', { timeOfDay: 'dusk', name: '현무암 능선(붉은 노을)' });
const R2_B: MapDef = EB_BASE;
const R2_B2: MapDef = mirrorMap(EB_BASE, 'b2', { timeOfDay: 'morning', name: '용암 강 다리(잿빛 새벽)' });
const R2_M: MapDef = EM_BASE;
const R2_F: MapDef = EF_BASE;
/** 지역2 스테이지 지도 6장(키 → 지도) */
const R2_STAGE_MAPS: Readonly<Record<StageMapKey, MapDef>> = { a: R2_A, a2: R2_A2, b: R2_B, b2: R2_B2, m: R2_M, f: R2_F };

// ═══ 레이드 1 — 잿불 둥지(RAID-PRD 4절) ═══════════════════════════════
// 남쪽 다리로 들어와 지름 38m 원형 현무암 섬에 오른다. 섬 둘레는 용암 바다(빈칸 = 못 지나간다). 안에는 막는 것이 없다
// (비행 브레스·화염 장판을 피할 자리를 가리지 않게 — 기둥은 섬 바깥 바다에 세운 장식). 방은 보스방 하나.
const RW_SEA = -2.8;
const RW_ARENA = disk(0, -46, 19);
const RW_FLOORS: Rect[] = union(r(-5, -4, 5, 5), r(-2, -28, 2, -4), RW_ARENA);
const RW_ROOMS: RoomSpec[] = [
  { name: '잿불 둥지', kind: 'boss', floor: RW_ARENA, area: bboxOf(RW_ARENA), gateIn: 0, gateOut: -1, waves: [], bossAt: { x: 0, z: -54 }, boss: 'ember' },
];
const RW_RAW: MapDef = {
  ...EMBER_BASE,
  threatClock: false,
  stageKey: 'f',
  name: '잿불 둥지',
  floors: RW_FLOORS,
  landmarks: [],
  gates: [{ rect: r(-2, -28, 2, -27), openAtStart: true }],
  rooms: emberRooms(RW_ROOMS, [], []),
  spawn: { x: 0, z: 0, facing: N },
  lava: [],
  seaY: RW_SEA,
  // C8-7 블라인드 P1 '배경 기둥·용암 바다 발광이 보스와 시선 경쟁': 기둥 균열 2.6→0.9배, 먼 바다 0.45배(1차 1.4·0.55는 실측 밝기 −7%로 부족).
  // 섬 가장자리 용암 테(떨어지면 위험)는 그대로 밝다 — 위험은 읽히고 배경만 물러난다
  envGlow: { sets: 0.9 / 2.6, seaFar: 0.45 },
  edgeZones: [{ rect: r(-3, -28, 3, -4), style: 'bare' }],
  sets: [
    { kind: 'e_bridge', x: 0, z: -16, rot: 0, len: 24 },
    // 섬을 둘러싼 현무암 기둥 두 겹(바다에서 솟는다) + 횃불 그릇 네 개(바깥 가장자리)
    // 보스 뒤(북쪽 ±15°)는 비워 둔다: 기본 카메라에서 비룡 실루엣이 기둥 숲에 묻혔다(L11 2026-10-30) — 그 자리는 용암 바다·먼 첨탑이 배경이 된다
    ...[...ringSets('e_basalt', 0, -46, 24, 20, 0.15, 2.6, RW_SEA - 1.5, 1), ...ringSets('e_basalt', 0, -46, 30, 14, 0.4, 3.6, RW_SEA - 2, 1)].filter((p) => !(p.z < -46 && Math.abs(p.x) < 7.5)),
    { kind: 'e_fire_bowl', x: -17, z: -36, rot: 0 }, { kind: 'e_fire_bowl', x: 17, z: -36, rot: 0 },
    { kind: 'e_fire_bowl', x: -17, z: -56, rot: 0 }, { kind: 'e_fire_bowl', x: 17, z: -56, rot: 0 },
    { kind: 'e_banner', x: -6, z: -66, rot: 0 }, { kind: 'e_banner', x: 6, z: -66, rot: 0 },
    ...emberFar(RW_FLOORS, 131, RW_SEA, 14, 6, 2, 40, 86),
  ],
};
/** 레이드 무대(레이드 id → 지도). 지도 id는 지역2(r2) 그대로 — 환경 세트·위험 테마를 지역2와 같이 쓴다 */
const RAID_MAPS: Readonly<Record<'raid_wyvern', MapDef>> = { raid_wyvern: withGateFrames(RW_RAW, 'e_gate', 4.6) };

  /** 스테이지 지도가 있는 지역 → 지도 6장 */
  const STAGE_MAPS: Readonly<Partial<Record<RegionId, Readonly<Record<StageMapKey, MapDef>>>>> = { r1: R1_STAGE_MAPS, r2: R2_STAGE_MAPS };
  return { R1_STAGE_MAPS, R2_STAGE_MAPS, RAID_MAPS, STAGE_MAPS };
}

let built: ReturnType<typeof buildStageMaps> | null = null;
/** 처음 부를 때 한 번만 만들고 같은 객체를 계속 돌려준다(지도 객체 동일성 — gridOfMap 캐시·toBe 비교가 이 성질에 기댄다) */
function maps(): ReturnType<typeof buildStageMaps> { return (built ??= buildStageMaps()); }
/** 지도 계산이 이미 일어났는지(시작 CPU 회귀 시험 전용) */
export function stageMapsBuilt(): boolean { return built !== null; }

/** 지역1 스테이지 지도 6장(키 → 지도) */
export function getR1StageMaps(): Readonly<Record<StageMapKey, MapDef>> { return maps().R1_STAGE_MAPS; }
/** 지역2 스테이지 지도 6장(키 → 지도) */
export function getR2StageMaps(): Readonly<Record<StageMapKey, MapDef>> { return maps().R2_STAGE_MAPS; }
/** 레이드 무대(레이드 id → 지도). 지도 id는 지역2(r2) 그대로 — 환경 세트·위험 테마를 지역2와 같이 쓴다 */
export function getRaidMaps(): Readonly<Record<'raid_wyvern', MapDef>> { return maps().RAID_MAPS; }
/** 스테이지 지도가 있는 지역 → 지도 6장 */
export function getStageMaps(): Readonly<Partial<Record<RegionId, Readonly<Record<StageMapKey, MapDef>>>>> { return maps().STAGE_MAPS; }

/** 스테이지 번호(1..10) → 지도 키(모든 지역 같은 규칙: 1·2 A, 3·4 A′, 5 M, 6·7 B, 8·9 B′, 10 F) */
export const stageMapKey = r1StageMapKey;
/** 지역·스테이지 번호 → 스테이지 지도(지도가 없는 지역은 null — 지역 지도 그대로 쓴다) */
export function stageMapOf(region: RegionId, index: number): MapDef | null {
  return getStageMaps()[region]?.[r1StageMapKey(index)] ?? null;
}

// 지도 객체별 판정 격자 캐시(스테이지 지도는 같은 지역 id를 쓰므로 id로 묶으면 섞인다)
const gridCache = new WeakMap<MapDef, ReturnType<typeof rasterize>>();
/** 지도 한 장의 판정 격자(한 번만 만든다) */
export function gridOfMap(map: MapDef): ReturnType<typeof rasterize> {
  let g = gridCache.get(map);
  if (!g) gridCache.set(map, (g = rasterize(map)));
  return g;
}

/** 걸을 수 있는 칸 수 */
export function walkableCells(map: MapDef): number {
  const g = gridOfMap(map);
  let n = 0;
  for (let i = 0; i < g.cells.length; i++) if (g.cells[i] === Cell.Floor) n++;
  return n;
}